// Overview view of a space (or folder): recent items, docs, bookmarks, folders, a lists table with
// progress and planning fields, shared files, and workload by status. Bookmarks and files live on the
// space (Space.bookmarks / Space.resources); list planning fields on the list.
import '@/styles/overview.css';
import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import type { List, Location, Task } from '@shared/types';
import { isCompleteType, PALETTE } from '@shared/constants';
import { newId } from '@shared/ids';
import { getDb, useDb, useDbShallow, useMe, type Tables } from '@/lib/store';
import { useUi } from '@/lib/ui';
import { foldersOf, listsOf, locationName, statusOf, tasksIn } from '@/lib/hierarchy';
import { locationPath, useOpenTask, useWorkspaceId } from '@/lib/nav';
import { createDoc, updateList, updateSpace } from '@/lib/actions';
import { formatBytes, formatDateShort } from '@/lib/format';
import {
  Bookmark as BookmarkIcon,
  CalendarPlus,
  CircleCheck,
  ExternalLink,
  File,
  FileText,
  Folder,
  Hash,
  ListTodo,
  Plus,
  Upload,
  UserPlus,
  X,
} from '@/components/ui/icons';
import { Popover } from '@/components/ui/Popover';
import { Avatar } from '@/components/ui/Avatar';
import { toast } from '@/components/ui/overlays';
import { DateRangeMenu } from '@/components/fields/DatePicker';
import { PriorityFlag, PriorityMenu } from '@/components/fields/Priority';
import { PeopleMenu } from '@/components/fields/People';
import { openDialog } from '@/shell/dialogs';
import { uploadFile } from '@/pages/chat/utils';
import { IMAGES_ONLY, MAX_UPLOAD_BYTES, sizeLabel, UPLOAD_ACCEPT } from '@shared/uploads';
import { keepUploadable } from '@/lib/upload';
import { useIsGuest } from '@/lib/roles';

/** Is `loc` inside the overview's scope (a space, or a folder)? The scope itself doesn't count. */
function inScope(db: Tables, scope: Location, loc: Location | null | undefined): boolean {
  if (!loc || (loc.type === scope.type && loc.id === scope.id)) return false;
  if (scope.type === 'space') {
    if (loc.type === 'folder') return db.folders[loc.id]?.spaceId === scope.id;
    if (loc.type === 'list') return db.lists[loc.id]?.spaceId === scope.id;
    return false;
  }
  if (scope.type === 'folder') return loc.type === 'list' && db.lists[loc.id]?.folderId === scope.id;
  return false;
}

/** Where a list or folder sits, for the "• in …" context. */
function parentName(db: Tables, loc: Location): string {
  if (loc.type === 'list') {
    const l = db.lists[loc.id];
    return l?.folderId ? (db.folders[l.folderId]?.name ?? '') : (db.spaces[l?.spaceId ?? '']?.name ?? '');
  }
  if (loc.type === 'folder') return db.spaces[db.folders[loc.id]?.spaceId ?? '']?.name ?? '';
  return '';
}

function Card({ title, className, actions, children }: { title: string; className?: string; actions?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className={clsx('ov-card', className)} aria-label={title}>
      <header className="ov-card-head">
        <span className="ov-card-title">{title}</span>
        <span className="spacer" />
        {actions}
      </header>
      <div className="ov-card-body">{children}</div>
    </section>
  );
}

function Empty({ icon, text, children }: { icon: React.ReactNode; text: string; children?: React.ReactNode }) {
  return (
    <div className="ov-empty">
      <span className="ov-empty-icon">{icon}</span>
      <p>{text}</p>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Recent & Docs

interface Row {
  key: string;
  icon: React.ReactNode;
  name: string;
  ctx: string;
  open: () => void;
}

function RecentCard({ scope }: { scope: Location }) {
  const ws = useWorkspaceId();
  const navigate = useNavigate();
  const openTask = useOpenTask();
  const recents = useUi((s) => s.recents);
  const db = useDb((d) => d);
  const rows = useMemo(() => {
    const out: Row[] = [];
    for (const r of recents) {
      if (out.length >= 8) break;
      if (r.kind === 'location' && r.location && inScope(db, scope, r.location)) {
        const loc = r.location;
        const list = loc.type === 'list' ? db.lists[loc.id] : undefined;
        out.push({
          key: `l:${loc.id}`,
          icon: loc.type === 'folder' ? <Folder size={15} /> : <ListTodo size={15} color={list?.color ?? undefined} />,
          name: locationName(db, loc),
          ctx: parentName(db, loc),
          open: () => navigate(locationPath(ws, loc)),
        });
      } else if (r.kind === 'task') {
        const t = db.tasks[r.id];
        if (t && !t.archived && inScope(db, scope, { type: 'list', id: t.listId })) {
          out.push({ key: `t:${t.id}`, icon: <CircleCheck size={15} />, name: t.name, ctx: db.lists[t.listId]?.name ?? '', open: () => openTask(t.id) });
        }
      } else if (r.kind === 'doc') {
        const d = db.docs[r.id];
        if (d && !d.archived && inScope(db, scope, d.location)) {
          out.push({ key: `d:${d.id}`, icon: <FileText size={15} />, name: d.title || 'Untitled', ctx: d.location ? locationName(db, d.location) : '', open: () => navigate(`/${ws}/docs/${d.id}`) });
        }
      } else if (r.kind === 'channel') {
        const c = db.channels[r.id];
        if (c && inScope(db, scope, c.location)) {
          out.push({ key: `c:${c.id}`, icon: <Hash size={15} />, name: c.name, ctx: c.location ? locationName(db, c.location) : '', open: () => navigate(`/${ws}/chat/${c.id}`) });
        }
      }
    }
    return out;
  }, [recents, db, scope, ws, navigate, openTask]);
  return (
    <Card title="Recent" className="ov-span-2 ov-fixed">
      {rows.length ? <RowList rows={rows} /> : <Empty icon={<CircleCheck size={22} />} text="Lists, tasks and docs you open in this space show up here." />}
    </Card>
  );
}

function RowList({ rows }: { rows: Row[] }) {
  return (
    <div className="ov-rows">
      {rows.map((r) => (
        <button key={r.key} type="button" className="ov-row" onClick={r.open}>
          <span className="ov-row-icon">{r.icon}</span>
          <span className="ov-row-name truncate">{r.name}</span>
          {r.ctx && <span className="ov-row-ctx truncate">• in {r.ctx}</span>}
        </button>
      ))}
    </div>
  );
}

function DocsCard({ scope }: { scope: Location }) {
  const ws = useWorkspaceId();
  const navigate = useNavigate();
  const docs = useDb((db) => db.docs);
  const db = useDb((d) => d);
  const rows: Row[] = useMemo(
    () =>
      Object.values(docs)
        .filter((d) => !d.archived && d.workspaceId === ws && (inScope(db, scope, d.location) || (d.location?.type === scope.type && d.location.id === scope.id)))
        .sort((a, b) => (b.updatedAt ?? b.createdAt) - (a.updatedAt ?? a.createdAt))
        .map((d) => ({ key: d.id, icon: <FileText size={15} />, name: d.title || 'Untitled', ctx: d.location ? locationName(db, d.location) : '', open: () => navigate(`/${ws}/docs/${d.id}`) })),
    [docs, db, ws, scope, navigate],
  );
  const newDoc = () => navigate(`/${ws}/docs/${createDoc(ws, scope).doc.id}`);
  return (
    <Card
      title="Docs"
      className="ov-span-2 ov-fixed"
      actions={
        <button type="button" className="icon-btn sm" title="New doc" aria-label="New doc" onClick={newDoc}>
          <Plus size={15} />
        </button>
      }
    >
      {rows.length ? (
        <RowList rows={rows} />
      ) : (
        <Empty icon={<FileText size={22} />} text="Docs you create in this space show up here.">
          <button type="button" className="btn sm outline" onClick={newDoc}>
            <Plus size={14} /> New doc
          </button>
        </Empty>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Bookmarks

const normalizeUrl = (raw: string) => {
  const v = raw.trim();
  if (!v) return '';
  return /^[a-z][a-z0-9+.-]*:/i.test(v) ? v : `https://${v}`;
};
const hostOf = (url: string) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

function BookmarkForm({ onAdd }: { onAdd: (title: string, url: string) => void }) {
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const u = normalizeUrl(url);
    if (!u) return;
    onAdd(title.trim() || hostOf(u), u);
  };
  return (
    <form className="ov-bm-form" onSubmit={submit}>
      <div className="ov-bm-form-title">Add bookmark</div>
      <input className="input" autoFocus placeholder="Paste a link" value={url} onChange={(e) => setUrl(e.target.value)} aria-label="Link" />
      <input className="input" placeholder="Name (optional)" value={title} onChange={(e) => setTitle(e.target.value)} aria-label="Name" />
      <button type="submit" className="btn sm primary" disabled={!url.trim()}>
        Add bookmark
      </button>
    </form>
  );
}

function BookmarksCard({ spaceId }: { spaceId: string }) {
  const me = useMe();
  const bookmarks = useDb((db) => db.spaces[spaceId]?.bookmarks) ?? [];
  const add = (title: string, url: string) => {
    const cur = getDb().spaces[spaceId]?.bookmarks ?? [];
    updateSpace(spaceId, { bookmarks: [...cur, { id: newId(), title, url, createdBy: me, createdAt: Date.now() }] });
  };
  const remove = (id: string) => updateSpace(spaceId, { bookmarks: (getDb().spaces[spaceId]?.bookmarks ?? []).filter((b) => b.id !== id) });
  const addButton = (label: React.ReactNode, className: string) => (
    <Popover placement="bottom-end" content={(close) => <BookmarkForm onAdd={(t, u) => (add(t, u), close())} />}>
      <button type="button" className={className}>
        {label}
      </button>
    </Popover>
  );
  return (
    <Card title="Bookmarks" className="ov-span-2 ov-fixed" actions={bookmarks.length ? addButton(<Plus size={15} />, 'icon-btn sm') : undefined}>
      {bookmarks.length ? (
        <div className="ov-rows">
          {bookmarks.map((b) => (
            <div key={b.id} className="ov-row ov-row-link">
              <a href={b.url} target="_blank" rel="noopener noreferrer" className="ov-row-main">
                <span className="ov-row-icon">
                  <BookmarkIcon size={15} />
                </span>
                <span className="ov-row-name truncate">{b.title}</span>
                <span className="ov-row-ctx truncate">{hostOf(b.url)}</span>
                <ExternalLink size={12} className="ov-row-ext" />
              </a>
              <button type="button" className="icon-btn xs ov-row-remove" title="Remove bookmark" aria-label={`Remove ${b.title}`} onClick={() => remove(b.id)}>
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <Empty icon={<BookmarkIcon size={22} />} text="Save links your team uses for this space: briefs, drives, references.">
          {addButton('Add bookmark', 'btn sm light')}
        </Empty>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Folders

function FoldersCard({ spaceId }: { spaceId: string }) {
  const ws = useWorkspaceId();
  const guest = useIsGuest();
  const navigate = useNavigate();
  const folders = useDbShallow((db) => foldersOf(db, spaceId).filter((f) => !f.archived));
  const listCounts = useDb((db) => JSON.stringify(folders.map((f) => listsOf(db, spaceId, f.id).length)));
  const counts = JSON.parse(listCounts) as number[];
  return (
    <Card title="Folders" className="ov-span-6">
      <div className="ov-folders">
        {folders.map((f, i) => (
          <button key={f.id} type="button" className="ov-folder" onClick={() => navigate(locationPath(ws, { type: 'folder', id: f.id }))}>
            <Folder size={18} color={f.color ?? undefined} />
            <span className="ov-folder-name truncate">{f.name}</span>
            <span className="ov-folder-count">{counts[i] === 1 ? '1 list' : `${counts[i]} lists`}</span>
          </button>
        ))}
        {!guest && (
          <button type="button" className="ov-folder add" onClick={() => openDialog({ kind: 'folder', spaceId })}>
            <Plus size={16} />
            <span>New folder</span>
          </button>
        )}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Lists table

function ListRow({ list, done, total }: { list: List; done: number; total: number }) {
  const ws = useWorkspaceId();
  const navigate = useNavigate();
  const pct = total ? Math.round((done / total) * 100) : 0;
  const dateCell = (field: 'startDate' | 'dueDate') => (
    <Popover
      content={(close) => (
        <DateRangeMenu
          start={list.startDate ?? null}
          due={list.dueDate ?? null}
          initialFocus={field === 'startDate' ? 'start' : 'due'}
          onChange={(startDate, dueDate) => updateList(list.id, { startDate, dueDate })}
          onClose={close}
        />
      )}
    >
      <button type="button" className={clsx('ov-cell-btn', { empty: !list[field] })} aria-label={field === 'startDate' ? 'Start date' : 'End date'}>
        {list[field] ? formatDateShort(list[field]) : <CalendarPlus size={15} />}
      </button>
    </Popover>
  );
  return (
    <div className="ov-lrow">
      <button type="button" className="ov-lname" onClick={() => navigate(locationPath(ws, { type: 'list', id: list.id }))}>
        <ListTodo size={15} color={list.color ?? undefined} />
        <span className="truncate">{list.name}</span>
      </button>
      <Popover
        content={(close) => (
          <div className="ov-swatches">
            <button type="button" className={clsx('ov-swatch none', { on: !list.color })} aria-label="No color" onClick={() => (updateList(list.id, { color: null }), close())}>
              <X size={11} />
            </button>
            {PALETTE.map((c) => (
              <button key={c} type="button" className={clsx('ov-swatch', { on: list.color === c })} style={{ background: c }} aria-label={c} onClick={() => (updateList(list.id, { color: c }), close())} />
            ))}
          </div>
        )}
      >
        <button type="button" className="ov-cell-btn" aria-label="List color">
          {list.color ? <span className="ov-color-dot" style={{ background: list.color }} /> : <span className="faint">–</span>}
        </button>
      </Popover>
      <div className="ov-progress" title={`${done} of ${total} tasks done`}>
        <span className="ov-progress-track">
          <span className="ov-progress-fill" style={{ width: `${pct}%` }} />
        </span>
        <span className="ov-progress-num">
          {done}/{total}
        </span>
      </div>
      {dateCell('startDate')}
      {dateCell('dueDate')}
      <Popover content={(close) => <PriorityMenu value={list.priority ?? null} onPick={(p) => (updateList(list.id, { priority: p }), close())} />}>
        <button type="button" className={clsx('ov-cell-btn', { empty: !list.priority })} aria-label="Priority">
          <PriorityFlag priority={list.priority ?? null} size={15} />
        </button>
      </Popover>
      <Popover content={(close) => <PeopleMenu selected={list.ownerId ? [list.ownerId] : []} single title="Owner" onChange={(ids) => (updateList(list.id, { ownerId: ids[0] ?? null }), close())} />}>
        <button type="button" className={clsx('ov-cell-btn', { empty: !list.ownerId })} aria-label="Owner">
          {list.ownerId ? <Avatar userId={list.ownerId} size={22} /> : <UserPlus size={15} />}
        </button>
      </Popover>
    </div>
  );
}

function ListsCard({ scope, spaceId }: { scope: Location; spaceId: string }) {
  const folderId = scope.type === 'folder' ? scope.id : null;
  const guest = useIsGuest();
  const lists = useDbShallow((db) => listsOf(db, spaceId, folderId).filter((l) => !l.archived));
  const tasks = useDb((db) => db.tasks);
  const db = useDb((d) => d);
  const progress = useMemo(() => {
    const out: Record<string, { done: number; total: number }> = {};
    for (const l of lists) out[l.id] = { done: 0, total: 0 };
    for (const t of Object.values(tasks)) {
      const p = out[t.listId];
      if (!p || t.archived) continue;
      p.total++;
      if (isCompleteType(statusOf(db, t).type)) p.done++;
    }
    return out;
  }, [lists, tasks, db]);
  return (
    <Card title="Lists" className="ov-span-6">
      <div className="ov-ltable" role="table">
        <div className="ov-lrow ov-lhead" role="row">
          <span>Name</span>
          <span>Color</span>
          <span>Progress</span>
          <span>Start</span>
          <span>End</span>
          <span>Priority</span>
          <span>Owner</span>
        </div>
        {lists.map((l) => (
          <ListRow key={l.id} list={l} done={progress[l.id]?.done ?? 0} total={progress[l.id]?.total ?? 0} />
        ))}
        {!guest && (
          <button type="button" className="ov-lnew" onClick={() => openDialog({ kind: 'list', spaceId, folderId })}>
            <Plus size={15} /> New list
          </button>
        )}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Resources (files shared on the space)

function ResourcesCard({ spaceId }: { spaceId: string }) {
  const me = useMe();
  const resources = useDb((db) => db.spaces[spaceId]?.resources) ?? [];
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(0);
  const add = async (picked: File[]) => {
    const files = keepUploadable(picked);
    if (!files.length) return;
    setBusy((n) => n + files.length);
    try {
      const refs = await Promise.all(files.map(uploadFile));
      const cur = getDb().spaces[spaceId]?.resources ?? [];
      updateSpace(spaceId, { resources: [...cur, ...refs.map((r) => ({ id: newId(), name: r.name, url: r.url, mime: r.mime, size: r.size, createdBy: me, createdAt: Date.now() }))] });
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setBusy((n) => n - files.length);
    }
  };
  const remove = (id: string) => updateSpace(spaceId, { resources: (getDb().spaces[spaceId]?.resources ?? []).filter((r) => r.id !== id) });
  return (
    <Card
      title="Resources"
      className="ov-span-3 ov-fixed"
      actions={
        resources.length ? (
          <button type="button" className="icon-btn sm" title="Add files" aria-label="Add files" onClick={() => input.current?.click()}>
            <Plus size={15} />
          </button>
        ) : undefined
      }
    >
      <div
        className={clsx('ov-drop', { over, filled: resources.length > 0 })}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          void add([...e.dataTransfer.files]);
        }}
      >
        {resources.length > 0 && (
          <div className="ov-rows">
            {resources.map((r) => (
              <div key={r.id} className="ov-row ov-row-link">
                <a href={r.url} target="_blank" rel="noopener noreferrer" className="ov-row-main">
                  <span className="ov-row-icon">
                    <File size={15} />
                  </span>
                  <span className="ov-row-name truncate">{r.name}</span>
                  {r.size !== undefined && <span className="ov-row-ctx">{formatBytes(r.size)}</span>}
                </a>
                <button type="button" className="icon-btn xs ov-row-remove" title="Remove file" aria-label={`Remove ${r.name}`} onClick={() => remove(r.id)}>
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="ov-drop-hint">
          <Upload size={15} />
          {busy > 0 ? (
            <span>Uploading…</span>
          ) : (
            <span>
              {IMAGES_ONLY ? `Drop images (up to ${sizeLabel(MAX_UPLOAD_BYTES)}) here or` : 'Drop files here or'}{' '}
              <button type="button" className="ov-link" onClick={() => input.current?.click()}>
                attach
              </button>
            </span>
          )}
        </div>
        <input
          ref={input}
          type="file"
          accept={UPLOAD_ACCEPT}
          multiple
          hidden
          onChange={(e) => {
            void add([...(e.target.files ?? [])]);
            e.target.value = '';
          }}
        />
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Workload by status: a donut for the part-to-whole read (≤ 6 slices, the rest folded into "Other")
// plus a legend that lists every status with its count and share.

interface Slice {
  key: string;
  name: string;
  color: string;
  count: number;
}

const MAX_SLICES = 6;

function arcPath(cx: number, cy: number, r: number, inner: number, a0: number, a1: number) {
  const p = (r0: number, a: number) => [cx + r0 * Math.sin(a), cy - r0 * Math.cos(a)];
  const large = a1 - a0 > Math.PI ? 1 : 0;
  const [x0, y0] = p(r, a0);
  const [x1, y1] = p(r, a1);
  const [x2, y2] = p(inner, a1);
  const [x3, y3] = p(inner, a0);
  return `M${x0} ${y0} A${r} ${r} 0 ${large} 1 ${x1} ${y1} L${x2} ${y2} A${inner} ${inner} 0 ${large} 0 ${x3} ${y3} Z`;
}

function WorkloadCard({ scope, className }: { scope: Location; className: string }) {
  const tasks = useDb((db) => db.tasks);
  const db = useDb((d) => d);
  const [hover, setHover] = useState<string | null>(null);
  const slices = useMemo(() => {
    const by = new Map<string, Slice>();
    for (const t of tasksIn(db, scope) as Task[]) {
      const s = statusOf(db, t);
      const key = s.name.toLowerCase();
      const cur = by.get(key) ?? { key, name: s.name, color: s.color, count: 0 };
      cur.count++;
      by.set(key, cur);
    }
    return [...by.values()].sort((a, b) => b.count - a.count);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, db, scope.type, scope.id]);
  const total = slices.reduce((n, s) => n + s.count, 0);
  const shown: Slice[] =
    slices.length > MAX_SLICES
      ? [...slices.slice(0, MAX_SLICES - 1), { key: '__other', name: 'Other', color: 'var(--text-4)', count: slices.slice(MAX_SLICES - 1).reduce((n, s) => n + s.count, 0) }]
      : slices;
  const sliceOf = (key: string) => (slices.slice(MAX_SLICES - 1).some((s) => s.key === key) && slices.length > MAX_SLICES ? '__other' : key);
  const focus = hover ? slices.find((s) => s.key === hover) ?? shown.find((s) => s.key === hover) : undefined;
  const pct = (n: number) => (total ? Math.round((n / total) * 100) : 0);

  let angle = 0;
  const R = 70;
  const INNER = 46;
  return (
    <Card title="Workload by status" className={className}>
      {total === 0 ? (
        <Empty icon={<CircleCheck size={22} />} text="No tasks here yet." />
      ) : (
        <div className="ov-workload">
          <svg viewBox="0 0 160 160" className="ov-donut" role="img" aria-label={`Tasks by status: ${slices.map((s) => `${s.name} ${s.count}`).join(', ')}`}>
            {shown.length === 1 ? (
              <circle cx="80" cy="80" r={(R + INNER) / 2} fill="none" stroke={shown[0].color} strokeWidth={R - INNER} />
            ) : (
              shown.map((s) => {
                const a0 = angle;
                const a1 = angle + (s.count / total) * Math.PI * 2;
                angle = a1;
                return (
                  <path
                    key={s.key}
                    d={arcPath(80, 80, R, INNER, a0, a1)}
                    fill={s.color}
                    className={clsx('ov-slice', { dim: hover !== null && sliceOf(hover) !== s.key })}
                    onMouseEnter={() => setHover(s.key)}
                    onMouseLeave={() => setHover(null)}
                  />
                );
              })
            )}
            <text x="80" y="76" textAnchor="middle" className="ov-donut-num">
              {focus ? focus.count : total}
            </text>
            <text x="80" y="94" textAnchor="middle" className="ov-donut-label">
              {focus ? `${pct(focus.count)}%` : total === 1 ? 'task' : 'tasks'}
            </text>
          </svg>
          <div className="ov-legend">
            {slices.map((s) => (
              <div key={s.key} className={clsx('ov-legend-row', { on: hover === s.key })} onMouseEnter={() => setHover(s.key)} onMouseLeave={() => setHover(null)}>
                <span className="ov-legend-dot" style={{ background: s.color }} />
                <span className="ov-legend-name truncate">{s.name}</span>
                <span className="ov-legend-count">{s.count}</span>
                <span className="ov-legend-pct">{pct(s.count)}%</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------

export function OverviewView({ location }: { location: Location }) {
  const spaceId = useDb((db) => (location.type === 'space' ? location.id : location.type === 'folder' ? db.folders[location.id]?.spaceId : undefined));
  if (!spaceId) return <div className="page-placeholder">Overview is available for spaces and folders.</div>;
  const isSpace = location.type === 'space';
  return (
    <div className="ov-page">
      <div className="ov-grid">
        <RecentCard scope={location} />
        <DocsCard scope={location} />
        {isSpace ? <BookmarksCard spaceId={spaceId} /> : <WorkloadCard scope={location} className="ov-span-2 ov-fixed" />}
        {isSpace && <FoldersCard spaceId={spaceId} />}
        <ListsCard scope={location} spaceId={spaceId} />
        {isSpace && <ResourcesCard spaceId={spaceId} />}
        {isSpace && <WorkloadCard scope={location} className="ov-span-3 ov-fixed" />}
      </div>
    </div>
  );
}
